<script setup>
import { ref, watch } from 'vue'
import { Head, router, useForm } from '@inertiajs/vue3'
import { PhDotsThreeCircle  } from "@phosphor-icons/vue"
import Layout  from '@pages/Shared/layouts/AdminLayout.vue'
import Modal   from '@pages/Shared/Modal.vue'
import Input   from '@pages/Shared/form/Input.vue'
import Prompt  from '@pages/Shared/Prompt.vue'
import Table   from '@pages/Shared/ui/Table.vue'
import Spinner from '@pages/Shared/ui/Spinner.vue'
import { flashToast } from "@/toast.js"

/* -------------------- PROPS -------------------- */
const props = defineProps({
    roles:   Object,
    filters: Object,
})

/* -------------------- STATE -------------------- */
const openModal   = ref(false)
const showPrompt  = ref(false)
const modalStatus = ref(null)
const modalTitle  = ref(null)
const filters     = ref({ search: null, type: null, ...props.filters })

/* -------------------- FORM -------------------- */
const form = useForm({ _method: null, id: null, name: '' })

/* -------------------- COLUMNS -------------------- */
const columns = [
    { key: 'index',   label: '#',    type: 'index'   },
    { key: 'name',    label: 'Name', type: 'slot'    },
    { key: 'actions', label: '',     type: 'actions' },
]

/* -------------------- FILTERS -------------------- */
watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined)
    )
    router.get('/admin/roles', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = (value) => { filters.value.search = value; applyFilters() }
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; applyFilters() }

/* -------------------- MODAL -------------------- */
const handleModalClose = () => {
    modalStatus.value = null
    modalTitle.value  = null
    openModal.value   = false
    form.clearErrors()
    form.reset()
}

const handleSubmit = () => {
    const options = {
        onSuccess: (page) => { handleModalClose(); flashToast(page.props.flash) },
        onError:   ()     => flashToast({ type: 'error', message: 'Check form for errors' }),
    }
    if (modalStatus.value === 'edit') {
        form._method = 'put'
        form.post(`/admin/roles/${form.id}`, options)
    } else {
        form.post('/admin/roles', options)
    }
}

const editRole = (id) => {
    const role = props.roles.data.find(r => r.id === id)
    if (!role) return
    form.id   = role.id
    form.name = role.name
    modalTitle.value  = 'Edit Role'
    modalStatus.value = 'edit'
    openModal.value   = true
}

const viewRole = (id) => {
    const role = props.roles.data.find(r => r.id === id)
    if (!role) return
    form.id   = role.id
    form.name = role.name
    modalTitle.value  = 'View Role'
    modalStatus.value = 'view'
    openModal.value   = true
}

const deleteRole = (id) => { form.id = id; showPrompt.value = true }

const confirmDelete = () => {
    form.delete(`/admin/roles/${form.id}`, {
        onSuccess: (page) => { showPrompt.value = false; form.reset(); flashToast(page.props.flash) },
    })
}
</script>

<template>
    <Layout>
        <Head title="Roles" />

        <Prompt
            title="role"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <Modal
            v-model="openModal"
            :title="modalTitle ?? 'Add Role'"
            @close="handleModalClose"
        >
            <form class="p-6 space-y-5" @submit.prevent="handleSubmit">
                <Input
                    v-model="form.name"
                    label="Role Name"
                    placeholder="e.g. Form Master"
                    :error="form.errors.name"
                    :disabled="modalStatus === 'view'"
                />
                <button
                    v-if="modalStatus !== 'view'"
                    type="submit"
                    :disabled="form.processing"
                    class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold transition
                           flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    :class="modalStatus === 'edit' ? 'bg-edit hover:bg-edit-light' : 'bg-primary hover:bg-primary-light'"
                >
                    <Spinner v-if="form.processing" :size="14" />
                    {{ modalStatus === 'edit' ? 'Update Role' : 'Add Role' }}
                </button>
            </form>
        </Modal>

        <!-- Header -->
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Roles</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage system roles and permissions</p>
            </div>
            <button
                @click="openModal = true"
                class="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-semibold
                    text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhDotsThreeCircle :size="18" /> Add Role
            </button>
        </div>

        <!-- Table -->
        <Table
            :data="roles"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            :filters="[
                {
                    key:     'type',
                    label:   'Type',
                    options: [
                        { label: 'All Types', value: null      },
                        { label: 'Admin',     value: 'admin'   },
                        { label: 'Staff',     value: 'staff'   },
                        { label: 'Student',   value: 'student' },
                    ],
                    value: filters.type,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @edit="editRole"
            @view="viewRole"
            @delete="deleteRole"
        >
            <template #name="{ row }">
                <span class="font-medium text-gray-800 capitalize">{{ row.name }}</span>
            </template>
        </Table>

    </Layout>
</template>